/**
 * Verify a bill — ported from the mobile app (Mobile/src/screens/admin/
 * VerifyTab.js + ScanPane.js). The customer holds up the barcode from their
 * digital bill; staff scan it or type the code beneath it, and the order is
 * fetched from the server. The code is only trusted because the server
 * returned the order for it — a barcode can be read off any screen.
 *
 * Three ways in, because a shop counter can have any of them:
 *   1. A USB or Bluetooth barcode scanner. These act as a keyboard — they type
 *      the code and press Enter — so the input keeps focus and checks on Enter.
 *   2. The camera, through @zxing/browser rather than the browser's own
 *      BarcodeDetector, which Safari (Mac and iPhone) does not have. It is
 *      loaded only when the camera starts, so shoppers never download it.
 *   3. Typing, for when neither is to hand.
 */
import React from 'react';
import { T, num, LOCALE } from './i18n';
import { orderIdFromCode } from './barcode';
import { customerName } from './orderView';

const STATUS = { pending: 'Pending', processing: 'Processing', in_progress: 'In progress', shipped: 'Shipped', out_for_delivery: 'Out for delivery', delivered: 'Delivered', completed: 'Completed', cancelled: 'Cancelled', refunded: 'Refunded' };

const cameraAvailable = () =>
  typeof navigator !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

const CSS = `
.lxv{max-width:560px}
.lxv-hint{font-size:13px;line-height:1.7;color:var(--lx-muted);margin:-8px 0 20px}
.lxv-scan{width:100%;height:54px;border-radius:27px;background:var(--lx-cream);color:var(--lx-bg);font-family:var(--sans);font-weight:600;font-size:14px;border:none;cursor:pointer}
.lxv-scan:disabled{opacity:.45;cursor:not-allowed}
.lxv-video{width:100%;border-radius:20px;background:#000;margin-top:12px;aspect-ratio:4/3;object-fit:cover}
.lxv-or{text-align:center;font-size:11px;letter-spacing:3px;color:var(--lx-muted);margin:18px 0 12px}
.lxv-row{display:flex;gap:8px}
.lxv-row input{flex:1;height:52px;border-radius:16px;border:1px solid var(--lx-line-strong);background:var(--lx-glass);color:var(--lx-cream);padding:0 16px;font-family:var(--sans);font-size:16px;letter-spacing:2px;direction:ltr;text-transform:uppercase;outline:none}
.lxv-row input:focus{border-color:var(--lx-gold)}
.lxv-row button{height:52px;padding:0 22px;border-radius:16px;border:none;background:linear-gradient(100deg,#F0DDAE,#C9A566);color:#14110E;font-family:var(--sans);font-weight:600;font-size:13px;cursor:pointer}
.lxv-row button:disabled{opacity:.5;cursor:not-allowed}
.lxv-res{margin-top:20px;border-radius:22px;padding:18px 20px;border:1px solid}
.lxv-res.ok{border-color:rgba(78,154,108,0.5);background:rgba(78,154,108,0.1)}
.lxv-res.bad{border-color:rgba(194,85,85,0.5);background:rgba(194,85,85,0.08)}
.lxv-res h3{font-family:var(--sans);font-weight:600;font-size:18px;margin-bottom:8px}
.lxv-res.ok h3{color:#4E9A6C}.lxv-res.bad h3{color:#C25555}
.lxv-line{display:flex;justify-content:space-between;gap:12px;font-size:13px;color:var(--lx-muted);margin:5px 0}
.lxv-line b{color:var(--lx-cream);font-weight:500;text-align:end}
.lxv-again{margin-top:14px;height:42px;padding:0 18px;border-radius:21px;border:1px solid var(--lx-line-strong);background:none;color:var(--lx-cream);font-family:var(--sans);font-size:13px;cursor:pointer}
`;

export default function VerifyAdminTab({ apiFetch, fmt }) {
  const [code, setCode] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null); // { ok, order } | { ok:false, reason }
  const [scanning, setScanning] = React.useState(false);
  const inputRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const controlsRef = React.useRef(null);
  const canScan = cameraAvailable();

  const check = React.useCallback(async raw => {
    const id = orderIdFromCode(raw);
    if (!id) { setResult({ ok: false, reason: T('That is not a Lumière bill code.') }); return; }
    setBusy(true); setResult(null);
    try {
      const d = await apiFetch(`/admin/orders/${id}`);
      const order = (d && d.order) || d;
      if (!order || order.id == null) setResult({ ok: false, reason: T('No order exists for that code.') });
      else setResult({ ok: true, order });
    } catch (e) {
      const msg = String((e && e.message) || '');
      setResult({ ok: false, reason: /404|not found/i.test(msg) ? T('No order exists for that code.') : (msg || T('Could not reach the server. Try again.')) });
    }
    setBusy(false);
    setCode('');
  }, [apiFetch]);

  /* Keep the input focused so a hardware scanner always has somewhere to type. */
  const refocus = React.useCallback(() => {
    if (!scanning && inputRef.current && !window.matchMedia('(pointer:coarse)').matches) inputRef.current.focus();
  }, [scanning]);
  React.useEffect(() => { refocus(); }, [refocus, result]);

  const stop = React.useCallback(() => {
    if (controlsRef.current) { try { controlsRef.current.stop(); } catch {} controlsRef.current = null; }
    setScanning(false);
  }, []);

  /* The reader attaches once the <video> has rendered, hence an effect. */
  React.useEffect(() => {
    if (!scanning || !videoRef.current) return undefined;
    let cancelled = false;
    import('@zxing/browser').then(({ BrowserMultiFormatReader }) => cancelled ? null :
    new BrowserMultiFormatReader().decodeFromConstraints({ video: { facingMode: 'environment' } }, videoRef.current, (res, _err, controls) => {
      if (cancelled || !res) return;
      const text = res.getText();
      if (!orderIdFromCode(text)) return; // the camera sees everything; only our codes count
      controls.stop(); controlsRef.current = null; setScanning(false);
      check(text);
    })).then(controls => {
      if (!controls) return;
      if (cancelled) controls.stop(); else controlsRef.current = controls;
    }).catch(e => {
      setScanning(false);
      setResult({ ok: false, reason: /denied|permission|NotAllowed/i.test(String(e && (e.name || e.message))) ? T('Camera access is off. Turn it on in your browser, or type the code instead.') : T('No camera on this device. Type the code beneath the barcode instead.') });
    });
    return () => {
      cancelled = true;
      if (controlsRef.current) { try { controlsRef.current.stop(); } catch {} controlsRef.current = null; }
    };
  }, [scanning, check]);
  React.useEffect(() => stop, [stop]);

  const scan = () => { if (scanning) { stop(); return; } setResult(null); setScanning(true); };

  const o = result && result.ok ? result.order : null;
  return (
    <div className="lxv" onClick={e => { if (e.target.tagName !== 'BUTTON' && e.target.tagName !== 'INPUT') refocus(); }}>
      <style>{CSS}</style>
      <header className="lxa-head"><div><div className="lx-kicker">{T('Counter')}</div><h2>{T('Verify a bill')}</h2></div></header>
      <p className="lxv-hint">{T("Scan the barcode on the customer's bill, or type the code beneath it. The order is then checked against the server.")}</p>
      <button className="lxv-scan" onClick={scan} disabled={!canScan}>{!canScan ? T('CAMERA SCANNING NOT SUPPORTED IN THIS BROWSER') : scanning ? T('STOP SCANNING') : T('SCAN A BILL')}</button>
      {scanning && <video ref={videoRef} className="lxv-video" muted playsInline/>}
      <div className="lxv-or">{T('OR TYPE THE CODE')}</div>
      <form className="lxv-row" onSubmit={e => { e.preventDefault(); check(code); }}>
        <input ref={inputRef} value={code} onChange={e => { setCode(e.target.value); setResult(null); }} placeholder="LUM-0000" aria-label={T('Bill code')} autoCapitalize="characters" autoComplete="off"/>
        <button type="submit" disabled={busy || !code.trim()}>{busy ? '…' : T('CHECK')}</button>
      </form>
      {result && (
        <div className={'lxv-res ' + (result.ok ? 'ok' : 'bad')} role="status">
          <h3>{result.ok ? '✓ ' + T('Genuine bill') : '✕ ' + T('Not verified')}</h3>
          {o ? <>
            <div className="lxv-line"><span>{T('Order')}</span><b dir="ltr">{o.order_number || o.id}</b></div>
            {/* The delivery name first, as on the bill: it is the person at the
                door, and it is the only name a guest order has. */}
            <div className="lxv-line"><span>{T('Customer')}</span><b>{customerName(o) || '—'}</b></div>
            <div className="lxv-line"><span>{T('Status')}</span><b>{T(STATUS[o.status] || o.status)}</b></div>
            <div className="lxv-line"><span>{T('Placed')}</span><b>{o.created_at ? new Date(o.created_at).toLocaleDateString(LOCALE, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</b></div>
            <div className="lxv-line"><span>{T('Items')}</span><b>{num((o.items || []).reduce((n, it) => n + Number(it.quantity || 0), 0))}</b></div>
            <div className="lxv-line"><span>{T('Total')}</span><b>{fmt(Number(o.total_amount || 0))}</b></div>
          </> : <div className="lxv-line"><span>{result.reason}</span></div>}
          <button className="lxv-again" onClick={() => { setResult(null); setCode(''); }}>{T('CHECK ANOTHER')}</button>
        </div>
      )}
    </div>
  );
}
